import {
  AfterContentInit,
  AfterViewChecked,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  forwardRef,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  QueryList,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Subject, merge, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { OptionComponent } from './option.component';
import { SelectTriggerDirective } from './select-trigger.directive';

@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  standalone: true,
  imports: [CommonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
})
export class SelectComponent
  implements ControlValueAccessor, AfterContentInit, OnDestroy, AfterViewChecked
{
  @Input() placeholder = 'Selecione...';

  @ContentChildren(OptionComponent) options!: QueryList<OptionComponent>;
  @ContentChild(SelectTriggerDirective) triggerTemplate?: SelectTriggerDirective;

  @HostBinding('class.app-select') readonly isSelect = true;

  @ViewChild('dropdown') dropdownRef?: ElementRef<HTMLDivElement>;

  isOpen = false;
  value: any = null;
  isDisabled = false;
  openUp = false;

  private onChange: (v: any) => void = () => {};
  private onTouched: () => void = () => {};
  private optionsSub?: Subscription;
  private destroy$ = new Subject<void>();
  private positionCalculated = false;

  constructor(private el: ElementRef<HTMLElement>) {}

  ngAfterViewChecked(): void {
    if (this.isOpen && !this.positionCalculated && this.dropdownRef) {
      this.calculateDropdownPosition();
      this.positionCalculated = true;
    }
  }

  private calculateDropdownPosition(): void {
    const hostRect = this.el.nativeElement.getBoundingClientRect();
    const dropdownEl = this.dropdownRef?.nativeElement;

    if (!dropdownEl) return;

    const dropdownHeight = dropdownEl.offsetHeight;
    const spaceBelow = window.innerHeight - hostRect.bottom;
    const spaceAbove = hostRect.top;

    this.openUp = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
  }

  get hasValue(): boolean {
    return this.value !== null && this.value !== undefined && this.value !== '';
  }

  get selectedOption(): OptionComponent | null {
    return this.options?.find((o) => o.value === this.value) ?? null;
  }

  get triggerContext() {
    return { $implicit: this.selectedOption };
  }

  ngAfterContentInit(): void {
    this.subscribeToOptions();
    this.options.changes.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.subscribeToOptions();
      this.updateSelectedState();
    });
    this.updateSelectedState();
  }

  private subscribeToOptions(): void {
    this.optionsSub?.unsubscribe();
    if (!this.options.length) return;
    this.optionsSub = merge(...this.options.map((o) => o.optionClick))
      .pipe(takeUntil(this.destroy$))
      .subscribe((opt) => this.onOptionClick(opt));
  }

  private updateSelectedState(): void {
    this.options?.forEach((o) => {
      o.isSelected = o.value === this.value;
    });
  }

  onOptionClick(opt: OptionComponent): void {
    this.value = opt.value;
    this.isOpen = false;
    this.openUp = false;
    this.positionCalculated = false;
    this.updateSelectedState();
    this.onChange(this.value);
    this.onTouched();
  }

  toggleDropdown(): void {
    if (!this.isDisabled) {
      this.isOpen = !this.isOpen;
      this.positionCalculated = false;

      if (!this.isOpen) {
        this.openUp = false;
      }
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.el.nativeElement.contains(event.target as Node)) {
      this.isOpen = false;
      this.openUp = false;
      this.positionCalculated = false;
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.isOpen) {
      this.positionCalculated = false;
      this.calculateDropdownPosition();
      this.positionCalculated = true;
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.isOpen) {
      this.positionCalculated = false;
      this.calculateDropdownPosition();
      this.positionCalculated = true;
    }
  }

  writeValue(val: any): void {
    this.value = val;
    this.updateSelectedState();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.optionsSub?.unsubscribe();
  }
}
